import { SpraypaintBase } from 'spraypaint';
import {
  RowData,
  SortingState,
  Table,
  TableOptions,
} from '@tanstack/react-table';
import { flow } from 'lodash';
import { ModelRecord } from 'spraypaint/lib-esm/model';
import { GraphitiScoper } from '@spraypaint/types';
import { useCallback, useMemo } from 'react';
import { TableStateManager } from '@common/components/table/types';
import { useReactStateTableStateManager } from '@common/components/table/hooks/useReactStateTableStateManager';
import { Tools, useControlledTable } from './useControlledTable';
import { useBackendSorting } from './useBackendSorting';
import { useBackendPagination } from './useBackendPagination';
import { useBackendFiltering } from './useBackendFiltering';

const defaultDebounceDelay = 500;

function getRowId<TData extends SpraypaintBase & RowData>(
  original: ModelRecord<TData>,
  index: number,
) {
  return original.id ?? original.temp_id ?? `i${index}`;
}

/*
  Use this to handle filtering, sorting and pagination in backend

  @usage
    const [scope, scopeKey, reactTableProps] = useBackendTable<CommercialContract>()
*/
export function useBackendTable<TData extends SpraypaintBase & RowData>({
  initialPageSize = 10,
  initialSorting = [],
  debounceDelay = defaultDebounceDelay,
  tableStateManager,
}: {
  initialPageSize?: number;
  initialSorting?: SortingState;
  paramKey?: string;
  debounceDelay?: number;
  tableStateManager?: TableStateManager;
} = {}): [
  scope: GraphitiScoper<TData>,
  scopeKey: Array<string>,
  reactTableProps: Partial<TableOptions<ModelRecord<TData>>>,
  tools: Tools,
] {
  const [
    { pagination, sorting, columnFilters },
    table,
    reactTablePropsBackend,
    tools,
    setTable,
  ] = useControlledTable<ModelRecord<TData>>({
    initialPageSize,
    initialSorting,
    tableStateManager: tableStateManager ?? useReactStateTableStateManager(),
  });

  const [paginate, paginationKey] = useBackendPagination<TData>(pagination);
  const [sort, sortingKey] = useBackendSorting<TData>(sorting);
  const [filter, filteringKey] = useBackendFiltering<TData>(
    columnFilters,
    table,
    debounceDelay,
  );

  const safeSetTable = useCallback(
    (newTable: Table<ModelRecord<TData>>) => {
      if (!table) {
        setTable(newTable);
      }
    },
    [table, setTable],
  );

  const reactTableProps = useMemo(
    () => ({
      ...reactTablePropsBackend,
      getRowId,
      setTable: safeSetTable,
    }),
    [reactTablePropsBackend, safeSetTable],
  );

  return [
    flow([filter, sort, paginate]),
    [sortingKey, paginationKey, filteringKey, !!table],
    {
      ...reactTableProps,
      getRowId,
    },
    tools,
  ];
}
